//core
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import format from "date-fns/format";
import { endOfDay, startOfDay } from "date-fns";
import { useSelector } from "react-redux";
import { useLocalStorage } from "usehooks-ts";
import {
  Box,
  SelectChangeEvent,
  useMediaQuery,
} from "@mui/material";

//styles
import { useStyles } from "./styles";

//helpers
import { getGroupByOptions, getDataIds, getPeriod, PeriodTypes } from "../../../../../utils";

//constants
import { periodOptions } from "../../../../../constant";
import { favoriteFilterKeys } from "../../../../constants";

//selectors
import { selectOptionsData } from "../../../../../../../../store/affiliate/reports/subaffiliate_report_v2/selectors";

//hooks
import { useSubaffiliateReportV2 } from "../../../../../../../../store/affiliate/reports/subaffiliate_report_v2/useSubaffiliateReportV2";
import { useRegistration } from "../../../../../../../../store/common/registration/useRegistration";

//context
import { GlobalContext } from "../../../../../../../../context/GlobalContext";

//types
import { ISubAffiliateReportV2FilterDataType } from "../../../../types";

//components
import CSelect from "../../../../../../../UI/Fields/CSelect";
import CDateRangePicker from "../../../../../../../UI/Fields/CDateRangePicker";
import GroupByField, { IItemOption } from "../../../GroupByField";
import MuiAutocompleteSelectLazy from "../../../../../../../UI/Fields/MuiAutocompleteSelectLazy";
import MuiAutocompleteSelect from "../../../../../../../UI/Fields/MuiAutocompleteSelect";
import MuiChipsInputField from "../../../../../../../UI/Fields/MuiChipsInput";

interface IVisibleFilterProps {
  filter: ISubAffiliateReportV2FilterDataType;
  setUpdateFilter: Dispatch<SetStateAction<ISubAffiliateReportV2FilterDataType>>;
}

const VisibleFilter: React.FC<IVisibleFilterProps> = ({
  filter,
  setUpdateFilter,
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const mobile = useMediaQuery('(max-width:767px)');

  const { getFiltersField } = useSubaffiliateReportV2();
  const { countries } = useRegistration();
  const optionsData = useSelector(selectOptionsData);
  const context = useContext(GlobalContext);

  //state
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodTypes>(filter.period);
  const [startDate, setStartDate] = useState<Date | ''>(getPeriod(filter.period).start);
  const [endDate, setEndDate] = useState<Date | ''>(getPeriod(filter.period).end);
  const [groupBy, setGroupBy] = useState<IItemOption[]>(filter.group_by.fields);
  const [dataCountries, setDataCountries] = useState<any | null>(null);

  //favorite fields state
  const [favParentAffiliateIds] = useLocalStorage<boolean>(favoriteFilterKeys.parentAffiliateIds, false);
  const [favReferredAffiliateIds] = useLocalStorage<boolean>(favoriteFilterKeys.referredAffiliateIds, false);
  const [favCommissionLevels] = useLocalStorage<boolean>(favoriteFilterKeys.commissionLevels, false);
  const [favDeals] = useLocalStorage<boolean>(favoriteFilterKeys.deals, false);
  const [favPlayerIds] = useLocalStorage<boolean>(favoriteFilterKeys.players, false);
  const [favBrands] = useLocalStorage<boolean>(favoriteFilterKeys.brands, false);
  const [favCurrencies] = useLocalStorage<boolean>(favoriteFilterKeys.currencies, false);
  const [favProductIds] = useLocalStorage<boolean>(favoriteFilterKeys.productIds, false);
  const [favPlatformIds] = useLocalStorage<boolean>(favoriteFilterKeys.platformIds, false);
  const [favGeo] = useLocalStorage<boolean>(favoriteFilterKeys.geo, false);

  useEffect(() => {
    if (!!countries.data) {
      const data = countries.data.reduce((acc: any[], item: any) => {
        acc.push({
          id: item.code,
          value: item.code,
        });
        return acc;
      }, []);
      setDataCountries({
        list: data,
        total_entries: data.length,
      });
    }
  }, [countries, i18n.language]);

  useEffect(() => {
    if (!!selectedPeriod) {
      const period = getPeriod(selectedPeriod);

      if (!!period.start && !!period.end) {
        setStartDate(period.start);
        setEndDate(period.end);

        setUpdateFilter((prevState) => ({
          ...prevState,
          period: selectedPeriod,
          sent_from: format(startOfDay(new Date(period.start)), "yyyy-MM-dd HH:mm:ss"),
          sent_to: format(endOfDay(new Date(period.end)), "yyyy-MM-dd HH:mm:ss"),
        }));
      }
    }
  }, [selectedPeriod]);

  useEffect(() => {
    setSelectedPeriod(filter.period);
  }, [filter.period]);

  useEffect(() => {
    setGroupBy(filter.group_by.fields);
  }, [filter.group_by.fields]);

  useEffect(() => {
    context.setAffSubAffiliateReportV2Filter(filter);
  }, [filter]);

  useEffect(() => {
    setUpdateFilter((prevState) => ({
      ...prevState,
      group_by: {
        fields: groupBy,
      },
    }));
  }, [groupBy]);

  const handleChangePeriodSelect = (event: SelectChangeEvent) => {
    //@ts-ignore
    const value: PeriodTypes = event.target.value;
    setSelectedPeriod(value);
  };

  const handleChangeDates = (start: Date, end: Date) => {
    const noEndDate = !end;

    setStartDate(start);
    setEndDate(noEndDate ? start : end);
    setSelectedPeriod('');
    setUpdateFilter((prevState) => ({
      ...prevState,
      period: '',
      sent_from: format(startOfDay(new Date(start)), "yyyy-MM-dd HH:mm:ss"),
      sent_to: format(endOfDay(new Date(noEndDate ? start : end)), "yyyy-MM-dd HH:mm:ss"),
    }));
  };

  const fieldPayload = () => ({
    page: 1,
    search: null,
    per_page: 100,
  });

  const fieldCurrenciesPayload = () => ({
    page: 1,
    search: null,
    per_page: 100,
    brandCodes: !!filter.brands.length ? getDataIds(filter.brands) : null,
  });

  const favoriteFields = !!dataCountries && !!optionsData && !mobile &&
    (favParentAffiliateIds || favReferredAffiliateIds || favCommissionLevels || favDeals || favPlayerIds ||
     favBrands || favCurrencies || favProductIds || favPlatformIds || favGeo)
    ? (
      <Box className={styles.favoriteFields}>
        {optionsData.availableFilters.includes("parent-affiliates") && favParentAffiliateIds && (
          <Box>
            <MuiAutocompleteSelectLazy
              keyValue="value" keyId="id" id="parentAffiliateIds" name="parentAffiliateIds"
              label={t(`admin.reports.filter.parentAffiliateIds`)}
              value={filter.parentAffiliateIds} multiple={true} showSelectAll={false} disabled={false}
              getOptions={getFiltersField} payloadData={fieldPayload()} labelWithId={true}
              onChange={setUpdateFilter} disableCloseOnSelect={true} showLabelCounter={true} showNameInPayload={true}
            />
          </Box>
        )}
        {optionsData.availableFilters.includes("referred-affiliates") && favReferredAffiliateIds && (
          <Box>
            <MuiAutocompleteSelectLazy
              keyValue="value" keyId="id" id="referredAffiliateIds" name="referredAffiliateIds"
              label={t(`admin.reports.filter.referredAffiliateIds`)}
              value={filter.referredAffiliateIds} multiple={true} showSelectAll={false} disabled={false}
              getOptions={getFiltersField} payloadData={fieldPayload()} labelWithId={true}
              onChange={setUpdateFilter} disableCloseOnSelect={true} showLabelCounter={true} showNameInPayload={true}
            />
          </Box>
        )}
        {optionsData.availableFilters.includes("commission-levels") && favCommissionLevels && (
          <Box>
            <MuiAutocompleteSelectLazy
              keyValue="value" keyId="id" id="commissionLevels" name="commissionLevels"
              label={t(`admin.reports.filter.commissionLevels`)}
              value={filter.commissionLevels} multiple={true} showSelectAll={false} disabled={false}
              getOptions={getFiltersField} payloadData={fieldPayload()} labelWithId={false}
              onChange={setUpdateFilter} disableCloseOnSelect={true} showLabelCounter={true} showNameInPayload={true}
            />
          </Box>
        )}
        {optionsData.availableFilters.includes("deals") && favDeals && (
          <Box>
            <MuiAutocompleteSelectLazy
              keyValue="value" keyId="id" id="deals" name="deals"
              label={t(`admin.reports.filter.deals`)}
              value={filter.deals} multiple={true} showSelectAll={false} disabled={false}
              getOptions={getFiltersField} payloadData={fieldPayload()} labelWithId={true}
              onChange={setUpdateFilter} disableCloseOnSelect={true} showLabelCounter={true} showNameInPayload={true}
            />
          </Box>
        )}
        {optionsData.availableFilters.includes("players") && favPlayerIds && (
          <Box>
            <MuiChipsInputField
              id="players" name="players"
              label={t(`admin.reports.filter.players`)}
              value={filter.players} onChange={setUpdateFilter}
            />
          </Box>
        )}
        {optionsData.availableFilters.includes("brands") && favBrands && (
          <Box>
            <MuiAutocompleteSelectLazy
              keyValue="value" keyId="id" id="brands" name="brands"
              label={t(`admin.reports.filter.brands`)}
              value={filter.brands} multiple={true} showSelectAll={false} disabled={false}
              getOptions={getFiltersField} payloadData={fieldPayload()} labelWithId={true}
              onChange={setUpdateFilter} disableCloseOnSelect={true} showLabelCounter={true} showNameInPayload={true}
            />
          </Box>
        )}
        {optionsData.availableFilters.includes("currencies") && favCurrencies && (
          <Box>
            <MuiAutocompleteSelectLazy
              keyValue="value" keyId="id" id="currencies" name="currencies"
              label={t(`admin.reports.filter.currencies`)}
              value={filter.currencies} multiple={true} showSelectAll={false} disabled={false}
              getOptions={getFiltersField} payloadData={fieldCurrenciesPayload()} labelWithId={false}
              onChange={setUpdateFilter} disableCloseOnSelect={true} showLabelCounter={true} showNameInPayload={true}
            />
          </Box>
        )}
        {optionsData.availableFilters.includes("products") && favProductIds && (
          <Box>
            <MuiAutocompleteSelectLazy
              keyValue="value" keyId="id" id="productIds" name="productIds"
              label={t(`admin.reports.filter.productIds`)}
              value={filter.productIds} multiple={true} showSelectAll={false} disabled={false}
              getOptions={getFiltersField} payloadData={fieldPayload()} labelWithId={false}
              onChange={setUpdateFilter} disableCloseOnSelect={true} showLabelCounter={true} showNameInPayload={true}
            />
          </Box>
        )}
        {optionsData.availableFilters.includes("platforms") && favPlatformIds && (
          <Box>
            <MuiAutocompleteSelectLazy
              keyValue="value" keyId="id" id="platformIds" name="platformIds"
              label={t(`admin.reports.filter.platformIds`)}
              value={filter.platformIds} multiple={true} showSelectAll={false} disabled={false}
              getOptions={getFiltersField} payloadData={fieldPayload()} labelWithId={false}
              onChange={setUpdateFilter} disableCloseOnSelect={true} showLabelCounter={true} showNameInPayload={true}
            />
          </Box>
        )}
        {optionsData.availableFilters.includes("geo") && favGeo && (
          <Box>
            <MuiAutocompleteSelect
              keyValue="value" keyId="id" id="geo" name="geo"
              label={t(`admin.reports.filter.geo`)}
              value={filter.geo} multiple={true} showSelectAll={false} disabled={false}
              staticOptions={dataCountries.list} onChange={setUpdateFilter}
              disableCloseOnSelect={true} maxHeightFieldScroll={32} showLabelCounter={true}
              prefix={`common.components.creative.list_geolocales.`}
            />
          </Box>
        )}
      </Box>
    ) : null;

  return (
    <>
      {!!optionsData && (
        <Box>
          <Box className={styles.wrapper}>
            <Box className={styles.box}>
              <CSelect
                name="period"
                label={t("affiliate.reports.filter.period")}
                value={selectedPeriod}
                options={periodOptions}
                onChange={handleChangePeriodSelect}
                prefix="common.components.periods."
              />
            </Box>
            <Box className={styles.boxDate}>
              <CDateRangePicker
                startDate={startDate}
                endDate={endDate}
                validateDates={true}
                onChange={handleChangeDates}
              />
            </Box>
          </Box>
          <Box className={styles.field}>
            <GroupByField
              name="groupBy"
              value={filter.group_by.fields}
              options={getGroupByOptions(optionsData.groupBy)}
              setFilter={setGroupBy}
              prefix={"admin.sub_affiliate_report_v2.filter.group_by_select."}
            />
          </Box>
          {favoriteFields}
        </Box>
      )}
    </>
  );
};

export default VisibleFilter;
